import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { S3Image } from '../../models/s3';
import { setColors } from '../../utils/set-bootstrap-colors';
import { GungCompanyService } from '../gung-company/gung-company.service';
import { ACTIVE_BRANDING_GROUP_RESOLVER } from './active-branding-group-resolver';
import { GungGroupBranding, GungGroupBrandingService } from './group-branding.service';

@Injectable({ providedIn: 'root' })
export class GroupBrandingApplyService {
  protected groupBrandingService = inject(GungGroupBrandingService);
  protected companyService = inject(GungCompanyService);
  protected resolver = inject(ACTIVE_BRANDING_GROUP_RESOLVER);

  private brandingForGroup(group: string | null): Observable<GungGroupBranding | null> {
    if (!group) {
      return of(null);
    }
    return this.groupBrandingService.getByGroup(group);
  }

  resolveMergedColors(group: string | null): Observable<{ [s: string]: string }> {
    return combineLatest([this.companyService.getPublicGungCompany(), this.brandingForGroup(group)]).pipe(
      map(([company, branding]) => ({ ...(company?.colors || {}), ...(branding?.colors || {}) }))
    );
  }

  resolveLogo(group: string | null): Observable<S3Image | null> {
    return combineLatest([this.companyService.getPublicGungCompany(), this.brandingForGroup(group)]).pipe(
      map(([company, branding]) => (branding?.logo?.s3Uri ? branding.logo : (company?.logo ?? null)))
    );
  }

  resolveBanner(group: string | null): Observable<any> {
    return combineLatest([this.companyService.getPublicGungCompany(), this.brandingForGroup(group)]).pipe(
      map(([company, branding]) => branding?.extra?.navbarBanner ?? company?.extra?.navbarBanner ?? null)
    );
  }

  /** Re-applies colours whenever the active group changes. */
  applyColorsForActiveGroup(): void {
    this.resolver
      .getActiveGroup()
      .pipe(switchMap(group => this.resolveMergedColors(group)))
      .subscribe(colors => {
        if (colors && Object.keys(colors).length > 0) {
          setColors(colors);
        }
      });
  }
}
